import DOMPurify from 'dompurify';
import { Button } from 'primereact/button';
import { useState } from 'react';

// Add CSS for email styling
const emailStyles = `
  .email-content img {
    max-width: 100%;
    height: auto;
  }
  .email-content a {
    color: #4caf50;
    text-decoration: underline;
  }
  .email-content table {
    border-collapse: collapse;
    width: 100%;
  }
  .email-content table, .email-content th, .email-content td {
    border: 1px solid #3a3a3a;
  }
  .email-content th, .email-content td {
    padding: 8px;
    text-align: left;
  }
`;

export const EmailContent = ({
  content,
  maxHeight,
  isExpanded,
}: {
  content?: string;
  maxHeight?: number;
  isExpanded: boolean;
}) => {
  const isHtml =
    content?.trim().toLowerCase().startsWith('<html') ||
    content?.includes('<div') ||
    content?.includes('<table') ||
    content?.includes('<p') ||
    content?.includes('<br');

  const contentStyle = {
    fontFamily: 'var(--font-family)',
    lineHeight: '1.5',
    color: 'white',
    fontSize: '0.85rem',
    ...(maxHeight && !isExpanded
      ? {
          maxHeight: `${maxHeight}px`,
          overflow: 'auto',
        }
      : {}),
  };

  if (isHtml) {
    const sanitizedHtml = DOMPurify.sanitize(content || '', {
      ALLOWED_TAGS: [
        'p',
        'ul',
        'ol',
        'li',
        'br',
        'strong',
        'em',
        'b',
        'i',
        'u',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'div',
        'span',
        'a',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'hr',
        'blockquote',
        'pre',
        'code',
        'sup',
        'sub',
      ],
      ALLOWED_ATTR: [
        'style',
        'href',
        'src',
        'alt',
        'title',
        'class',
        'id',
        'target',
        'width',
        'height',
        'border',
        'cellpadding',
        'cellspacing',
        'colspan',
        'rowspan',
      ],
      ALLOW_UNKNOWN_PROTOCOLS: true,
    });

    return (
      <div
        className="email-content"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        style={contentStyle}
      />
    );
  }

  return (
    <div
      className="email-content"
      style={{
        whiteSpace: 'pre-wrap',
        ...contentStyle,
      }}
    >
      {content}
    </div>
  );
};

// Standalone component for rendering email views - only responsible for displaying the email
export const EmailView = ({
  fromAddress,
  toAddress,
  subject,
  content,
  maxHeight,
}: {
  fromAddress?: string;
  toAddress?: string;
  subject?: string;
  content?: string;
  maxHeight?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="border-round shadow-1"
      style={{
        backgroundColor: '#1e1e1e',
        color: 'white',
      }}
    >
      <style>{emailStyles}</style>

      <div className="p-2 border-round-top bg-bluegray-900">
        <div className="flex justify-content-between align-items-start">
          <div className="flex flex-column gap-1">
            <div>
              <span className="text-sm text-bluegray-400 w-4rem inline-block">
                From:
              </span>
              <span className="text-sm">{fromAddress}</span>
            </div>
            <div>
              <span className="text-sm text-bluegray-400 w-4rem inline-block">
                To:
              </span>
              <span className="text-sm">{toAddress}</span>
            </div>
            <div>
              <span className="text-sm text-bluegray-400 w-4rem inline-block">
                Subject:
              </span>
              <span className="text-sm">{subject}</span>
            </div>
          </div>
          {maxHeight && (
            <Button
              icon={isExpanded ? 'pi pi-compress' : 'pi pi-expand'}
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-button-text p-button-sm"
              tooltipOptions={{
                position: 'top',
              }}
              tooltip={
                isExpanded
                  ? 'Collapse email content'
                  : 'Show full email content'
              }
            />
          )}
        </div>
      </div>
      <div className="p-2 bg-bluegray-800">
        <EmailContent
          content={content}
          maxHeight={maxHeight}
          isExpanded={isExpanded}
        />
      </div>
    </div>
  );
};
